// Core hook: one competition's bracket (GET /public/tournaments/:id). This is
// today's components/BracketWidget fetch, lifted verbatim (same effect, same
// deps, same cancellation) so a template's standings section can render the
// tree for a `kind: 'bracket'` featured item. Classic's BracketWidget moves
// onto it during extraction; a template calls it only where it shows a
// bracket, so the request fires exactly when that view mounts, as today.

import { useEffect, useState } from 'react'
import { api } from '@/services/api'
import type { TournamentDetails } from '@/services/api/types'

export interface CompetitionBracketState {
  status: 'loading' | 'ready' | 'error' | 'empty'
  data: TournamentDetails | null
  loading: boolean
  error: string | null
}

export function useCompetitionBracket(competitionId: number): CompetitionBracketState {
  const [data, setData] = useState<TournamentDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .getTournament(competitionId)
      .then((res) => {
        if (cancelled) return
        setData(res)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load bracket')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [competitionId])

  const status: CompetitionBracketState['status'] = loading
    ? 'loading'
    : error
      ? 'error'
      : !data || !data.games || data.games.length === 0
        ? 'empty'
        : 'ready'
  return { status, data, loading, error }
}
